import type { Coordinate, GeoData } from "./geo.ts";

export const MAX_GUESSES = 6;
export type Band = "correct" | "nextDoor" | "hot" | "warm" | "mild" | "far";
export type Status = "playing" | "won" | "lost";
export interface GuessResult { country: string; distanceKm: number; direction: number | null; band: Band }
export interface Clue { kind: "continent" | "region" | "hint"; value: string }

export function band(km: number, correct: boolean): Band {
  if (correct) return "correct";
  if (km < 60) return "nextDoor";
  if (km <= 1000) return "hot";
  if (km <= 2500) return "warm";
  if (km <= 5000) return "mild";
  return "far";
}

/** Eight-point direction from a to b (bearing between centroids); 0 = north, clockwise. */
export function compass(a: Coordinate, b: Coordinate): number {
  const rad = Math.PI / 180;
  const [l1, p1, l2, p2] = [a.lon * rad, a.lat * rad, b.lon * rad, b.lat * rad];
  const y = Math.sin(l2 - l1) * Math.cos(p2);
  const x = Math.cos(p1) * Math.sin(p2) - Math.sin(p1) * Math.cos(p2) * Math.cos(l2 - l1);
  const degrees = ((Math.atan2(y, x) / rad + 360) % 360);
  return Math.round(degrees / 45) % 8;
}

export function resultFor(answer: string, id: string, geo: GeoData): GuessResult | undefined {
  if (id === answer) return geo.country(id) ? { country: id, distanceKm: 0, direction: null, band: "correct" } : undefined;
  const from = geo.country(id), to = geo.country(answer), km = geo.distanceKm(id, answer);
  if (!from || !to || km === undefined) return undefined;
  return { country: id, distanceKm: km, direction: compass(from.centroid, to.centroid), band: band(km, false) };
}

export const results = (answer: string, guesses: string[], geo: GeoData): GuessResult[] =>
  guesses.flatMap((g) => resultFor(answer, g, geo) ?? []);

export function status(answer: string, guesses: string[]): Status {
  if (guesses.includes(answer)) return "won";
  return guesses.length >= MAX_GUESSES ? "lost" : "playing";
}

export const wrongCount = (answer: string, guesses: string[]): number => guesses.filter((g) => g !== answer).length;

export const dimsOtherContinents = (answer: string, guesses: string[]): boolean =>
  status(answer, guesses) === "playing" && wrongCount(answer, guesses) >= 2;

/** Continent after 2 wrong guesses, region after 3, the hint after 4. */
export function clues(answer: string, hint: string, guesses: string[], geo: GeoData): Clue[] {
  const c = geo.country(answer);
  if (!c) return [];
  const wrong = wrongCount(answer, guesses);
  const out: Clue[] = [];
  if (wrong >= 2) out.push({ kind: "continent", value: c.continent });
  if (wrong >= 3) out.push({ kind: "region", value: c.region });
  if (wrong >= 4) out.push({ kind: "hint", value: hint });
  return out;
}

/** Where the globe faces after a guess (the app's GlobeFocus): the answer once over, its continent at the second miss, else the last guess. */
export function focus(answer: string, guesses: string[], geo: GeoData): Coordinate | undefined {
  if (status(answer, guesses) !== "playing") return geo.country(answer)?.centroid;
  if (wrongCount(answer, guesses) === 2) {
    const continent = geo.country(answer)?.continent;
    return continent === undefined ? undefined : geo.continentCentre(continent);
  }
  const last = guesses.at(-1);
  return last === undefined ? undefined : geo.country(last)?.centroid;
}
